
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDateRange } from "@/utils/dateRangeUtils";

export const useTopClickedProducts = (selectedDateRange: string) => {
  const dateRange = getDateRange(selectedDateRange);

  return useQuery({
    queryKey: ['top-clicked-products', selectedDateRange],
    queryFn: async () => {
      // First get clicks with vacation details
      const { data: clicksData, error: clicksError } = await supabase
        .from('search_analytics')
        .select('clicked_vacation_id, vacation_position')
        .eq('interaction_type', 'click')
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end)
        .not('clicked_vacation_id', 'is', null);
      
      if (clicksError) throw clicksError;
      
      // Group by vacation ID and count clicks
      const clickCounts: Record<number, { count: number; totalPosition: number; positions: number[] }> = {};
      clicksData.forEach(item => {
        const vacationId = item.clicked_vacation_id!;
        if (!clickCounts[vacationId]) {
          clickCounts[vacationId] = { count: 0, totalPosition: 0, positions: [] };
        }
        clickCounts[vacationId].count++;
        if (item.vacation_position) {
          clickCounts[vacationId].positions.push(item.vacation_position);
          clickCounts[vacationId].totalPosition += item.vacation_position;
        }
      });
      
      // Get vacation details for the clicked products
      const vacationIds = Object.keys(clickCounts).map(id => parseInt(id));
      if (vacationIds.length === 0) return [];
      
      // Query the Complete feed table with the correct product ID field
      // Convert number array to string array since product ID is text type
      const { data: vacationsData, error: vacationsError } = await supabase
        .from('Complete feed')
        .select('*')
        .in('product ID', vacationIds.map(id => id.toString()));
      
      if (vacationsError) throw vacationsError;
      
      // Combine click data with vacation details
      return Object.entries(clickCounts)
        .map(([vacationId, clickData]) => {
          const vacation = vacationsData?.find((v: any) => v['product ID'] === vacationId);
          
          // Truncate long product names to max 50 characters
          const truncateName = (name: string) => {
            if (!name) return `Product ${vacationId}`;
            return name.length > 50 ? `${name.substring(0, 47)}...` : name;
          };
          
          return {
            vacationId: parseInt(vacationId),
            vacationName: truncateName(vacation?.name || `Product ${vacationId}`),
            clickCount: clickData.count,
            productUrl: vacation?.productURL,
            avgPosition: clickData.positions.length > 0 
              ? clickData.totalPosition / clickData.positions.length 
              : 0
          };
        })
        .sort((a, b) => b.clickCount - a.clickCount)
        .slice(0, 10);
    },
    refetchInterval: selectedDateRange === 'today' ? 60000 : undefined
  });
};
